import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { apiCall, ApiHttpError, RouteNotFoundError } from "../api/client";
import { AuthRequired } from "../components/AuthRequired";
import { clearSession, getSessionToken } from "../auth/session";
import { serverLogout } from "../auth/runtime";
import { parseServerSession, type ServerSessionClaims } from "../auth/session-claims";
import { SessionIdentity } from "../components/SessionIdentity";
import { Card, Disclosure, SectionLabel } from "../ui/primitives";

interface BffSessionResponse {
  session?: {
    sessionId?: string;
    ownerSubject?: string;
    walletAddress?: string;
    deviceId?: string;
    expiresAt?: string;
    lastSeenAt?: string;
  };
  [k: string]: unknown;
}

interface BffMeResponse {
  owner?: unknown;
  session?: unknown;
  pendingProposalsCount?: number;
  [k: string]: unknown;
}

export function SessionStatus() {
  return (
    <div>
      <PageHeader
        title="Session"
        subtitle="Read-only inspection of the current bearer session and the operator's pending-proposals count."
        method="GET"
        path="/v2/admin/bff/session, /v2/admin/bff/me, POST /v2/admin/bff/logout"
      />
      <AuthRequired>
        <SessionAuthed />
      </AuthRequired>
    </div>
  );
}

function SessionAuthed() {
  const navigate = useNavigate();
  const [session, setSession] = useState<BffSessionResponse | null>(null);
  // The same projection, parsed. Rendered as the identity block so this page
  // and the sidebar cannot disagree about who is signed in.
  const [claims, setClaims] = useState<ServerSessionClaims | null>(null);
  const [me, setMe] = useState<BffMeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  async function refresh() {
    setError(null);
    try {
      const [s, m] = await Promise.all([
        apiCall<BffSessionResponse>("/v2/admin/bff/session", { method: "GET" }),
        apiCall<BffMeResponse>("/v2/admin/bff/me", { method: "GET" }),
      ]);
      setSession(s);
      setClaims(parseServerSession(s));
      setMe(m);
    } catch (e) {
      setError(formatError(e));
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onLogout() {
    setLoggingOut(true);
    setError(null);
    const bearer = getSessionToken();
    try {
      await serverLogout(bearer);
    } catch (e) {
      setError(formatError(e));
    } finally {
      clearSession();
      setLoggingOut(false);
      navigate("/login");
    }
  }

  return (
    <div>
      <div style={{ display: "flex", gap: 12 }}>
        <button onClick={refresh}>Refresh</button>
        <button onClick={onLogout} disabled={loggingOut}>
          {loggingOut ? "Logging out…" : "Logout"}
        </button>
        <Link to="/login" style={{ alignSelf: "center" }}>Re-login</Link>
      </div>
      {error && <ErrorBox text={error} />}

      {/* The server's own account of this session, first. The raw envelopes
          below are diagnostics — §2 keeps session ids and RPC detail behind a
          disclosure, and this page is where an operator goes looking for them. */}
      {claims !== null && (
        <div style={{ marginTop: 12 }}>
          <Card>
            <div style={{ display: "grid", gap: 12 }}>
              <SectionLabel>Operator identity</SectionLabel>
              <SessionIdentity
                session={{
                  walletAddress: claims.walletAddress ?? "",
                  ownerSubject: claims.ownerSubject ?? "—",
                  savedAt: 0,
                  expiresAt: null,
                  authority: claims.authority,
                }}
              />
            </div>
          </Card>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <Disclosure summary="Advanced diagnostics">
          <Block title="GET /v2/admin/bff/session" body={session} />
          <Block title="GET /v2/admin/bff/me" body={me} />
        </Disclosure>
      </div>
    </div>
  );
}

function Block({ title, body }: { title: string; body: unknown }) {
  return (
    <fieldset
      style={{
        border: "1px solid var(--border)",
        borderRadius: 4,
        padding: 12,
        marginTop: 12,
      }}
    >
      <legend style={{ padding: "0 6px", color: "var(--fg-dim)" }}>{title}</legend>
      {body === null ? <em>(no data)</em> : <pre>{JSON.stringify(body, null, 2)}</pre>}
    </fieldset>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: 12,
        border: "1px solid var(--danger)",
        color: "var(--danger)",
        borderRadius: 4,
        background: "rgba(255,100,100,0.08)",
      }}
    >
      {text}
    </div>
  );
}

function formatError(e: unknown): string {
  if (e instanceof RouteNotFoundError) return `BE route missing: ${e.message}`;
  if (e instanceof ApiHttpError) return `BE HTTP ${e.status} on ${e.path}`;
  return e instanceof Error ? e.message : String(e);
}
